"use client";
/*
 * QueryBadge — Badge component specifically for Queries Section
 * Handles source, status, and priority variants
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";

interface QueryBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  source?: "general" | "invoice" | "bill" | "transaction" | "journal" | "credit_note" | "compliance";
  status?: "open" | "in_progress" | "closed" | "resolved";
  priority?: "low" | "medium" | "high";
  icon?: React.ReactNode;
  children?: React.ReactNode;
  iconRight?: React.ReactNode;
  className?: string;
}

const QueryBadge = React.forwardRef<HTMLDivElement, QueryBadgeProps>(
  function QueryBadge(
    {
      source,
      status,
      priority,
      icon = null,
      children,
      iconRight = null,
      className,
      ...otherProps
    }: QueryBadgeProps,
    ref
  ) {
    // Determine background and border colors based on props
    const getBackgroundClasses = () => {
      if (source) {
        switch (source) {
          case "general":
            return "border-success-100 bg-success-100";
          case "invoice":
            return "border-warning-100 bg-warning-100";
          case "bill":
            return "border-error-100 bg-error-100";
          case "transaction":
            return "border-neutral-100 bg-neutral-100";
          case "journal":
            return "border-neutral-100 bg-neutral-100";
          case "credit_note":
            return "border-brand-100 bg-brand-100";
          case "compliance":
            return "border-brand-100 bg-brand-100";
          default:
            return "border-brand-100 bg-brand-100";
        }
      }
      return "border-brand-100 bg-brand-100";
    };

    // Determine text color based on props
    const getTextColor = () => {
      if (status) {
        switch (status) {
          case "open":
            return "text-success-800";
          case "in_progress":
            return "text-warning-800";
          case "closed":
            return "text-error-800";
          case "resolved":
            return "text-neutral-700";
          default:
            return "text-brand-800";
        }
      }
      if (priority) {
        switch (priority) {
          case "low":
            return "text-success-800";
          case "medium":
            return "text-neutral-700";
          case "high":
            return "text-warning-800";
          default:
            return "text-brand-800";
        }
      }
      return "text-brand-800";
    };

    // Determine icon color based on props
    const getIconColor = () => {
      if (status) {
        switch (status) {
          case "open":
            return "text-success-800";
          case "in_progress":
            return "text-warning-800";
          case "closed":
            return "text-error-700";
          case "resolved":
            return "text-neutral-700";
          default:
            return "text-brand-700";
        }
      }
      if (priority) {
        switch (priority) {
          case "low":
            return "text-success-800";
          case "medium":
            return "text-neutral-700";
          case "high":
            return "text-warning-800";
          default:
            return "text-brand-700";
        }
      }
      return "text-brand-700";
    };

    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/query-badge flex h-6 items-center gap-1 rounded-md border border-solid px-2",
          getBackgroundClasses(),
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {icon ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames(
              "text-caption font-caption",
              getIconColor()
            )}
          >
            {icon}
          </SubframeCore.IconWrapper>
        ) : null}
        {children ? (
          <span
            className={SubframeUtils.twClassNames(
              "whitespace-nowrap text-caption font-caption",
              getTextColor()
            )}
          >
            {children}
          </span>
        ) : null}
        {iconRight ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames(
              "text-caption font-caption",
              getIconColor()
            )}
          >
            {iconRight}
          </SubframeCore.IconWrapper>
        ) : null}
      </div>
    );
  }
);

export { QueryBadge };
